/**
 * Unified report-field resolver for the Coverage Matrix detail panel.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { ExtractedClaim } from '../../types/siteContext';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { isFieldFilled, isAuthoredValuePresent } from '../../types/authoredEvidence';
import {
  getAlignedFieldPaths,
  getFieldAlignment,
  resolveAlignedSedField,
} from './requirementFieldAlignment';
import { resolveAutoFillOverlayField } from './autoFillOverlay';
import { resolveWetlandAcreage } from '../reportFormatters';
import {
  resolveRequirementFields,
  formatFieldValue,
  type FieldPath,
  type FieldProvenanceKind,
  type ResolvedField,
} from '../evidenceLineage/requirementToFields';

export type ReportFieldProvenance = FieldProvenanceKind | 'authored' | 'missing';

export interface ReportPrimaryField {
  key: string;
  label: string;
  value: unknown;
  displayValue: string;
  provenance: ReportFieldProvenance;
  sourceLabel?: string;
  required: boolean;
  editable: boolean;
  isEmpty: boolean;
  authoredBy?: string;
  authoredAt?: string;
}

export interface ReportRelatedField {
  path: FieldPath;
  label: string;
  value: unknown;
  displayValue: string;
  provenance: FieldProvenanceKind;
  sourceLabel?: string;
  editable: false;
  isEmpty: boolean;
  /** Related context is visible but cannot fill the applicable requirement. */
  contextOnly?: boolean;
}

export interface ResolveReportFieldsResult {
  primary: ReportPrimaryField[];
  related: ReportRelatedField[];
  hasSchema: boolean;
  /** Filled structured report fields (notes excluded). */
  structuredFilled: number;
  /** Total structured report fields (notes excluded). */
  structuredTotal: number;
  /**
   * Status fraction for matrix chips and completeness bar — all report
   * fields (including notes) plus related site-data registry paths.
   */
  statusFilled: number;
  statusTotal: number;
}

/** Matrix chip + completeness bar: (primary + related filled) / (primary + related total). */
export function coverageStatusFraction(
  resolved: Pick<ResolveReportFieldsResult, 'primary' | 'related'>,
): { filled: number; total: number } {
  const primaryFilled = resolved.primary.filter(f => !f.isEmpty).length;
  const relatedFilled = resolved.related.filter(f => !f.contextOnly && !f.isEmpty).length;
  return {
    filled: primaryFilled + relatedFilled,
    total: resolved.primary.length + resolved.related.filter(f => !f.contextOnly).length,
  };
}

/** These requirements ask for an applicable tariff/PPA, not a state average. */
export function isTariffRequirement(requirementId: string): boolean {
  return requirementId === 'tariff' || requirementId === 'retail-tariff-rate';
}

/** Aggregate section provenance cannot turn an EIA average into a document fact. */
export function isContextualTariffField(
  requirementId: string, field: ResolvedField, sed: SiteEvidenceDocument | undefined,
): boolean {
  if (!isTariffRequirement(requirementId) || field.sectionId !== 'electricity') return false;
  if (field.path !== 'electricity.retailRate') return true;
  if (field.isEmpty || field.provenance === 'authored') return false;
  const section = sed?.electricity;
  const leaf = section?.docFieldSources?.retailRate;
  const documentBound = typeof leaf?.docId === 'string' && leaf.docId.trim().length > 0
    && Object.is(leaf.value, field.value);
  if (documentBound) return false;
  // A legacy pure-document slot remains valid. Mixed/provider/research/DAM
  // provenance, or an explicit average basis, needs an actual matching leaf.
  const basis = (section?.data as unknown as { basis?: unknown } | undefined)?.basis;
  return basis === 'state-industrial-average'
    || ['provider', 'provider+doc', 'dam', 'research'].includes(section?.provenance ?? '')
    || (field.provenance !== 'doc' && field.provenance !== 'doc-override');
}

const QUERY_WETLAND_PATHS: ReadonlySet<FieldPath> = new Set([
  'environmental.hasWetlands', 'environmental.wetlandAcres', 'environmental.wetlandType',
]);

/** Full source-feature totals are query context, not a parcel delineation. */
function isQueryWetlandField(sed: SiteEvidenceDocument | undefined, path: FieldPath): boolean {
  return sed?.environmental?.data?.wetlandAcreageBasis === 'source-feature-total'
    && QUERY_WETLAND_PATHS.has(path);
}

/** Unmapped whole-section payloads are source details, never a fact answer. */
function isStructuralSectionExtra(field: ResolvedField): boolean {
  return field.path === field.sectionId
    && (field.value == null || typeof field.value === 'object');
}

function findSedFieldForSchemaKey(
  sed: SiteEvidenceDocument | undefined,
  requirementId: string,
  schemaKey: string,
  schemaLabel: string,
  sedFields: ResolvedField[],
): ResolvedField | undefined {
  // The Wetlands worksheet asks about the site. Keep explicit authored or
  // document answers on their own rungs; a regional inventory cannot fill it.
  if (requirementId === 'wetland' && getAlignedFieldPaths(requirementId, schemaKey)
    .some(path => isQueryWetlandField(sed, path))) return undefined;
  const aligned = resolveAlignedSedField(sed, requirementId, schemaKey);
  if (aligned && !isContextualTariffField(requirementId, aligned, sed)) return aligned;

  const alignment = getFieldAlignment(requirementId, schemaKey);
  if (alignment?.fieldPath) {
    for (const path of [alignment.fieldPath, ...(alignment.fallbackFieldPaths ?? [])]) {
      const byPath = sedFields.find(f => f.path === path && !f.isEmpty);
      if (byPath && !isContextualTariffField(requirementId, byPath, sed)) return byPath;
    }
  }
  return sedFields.find(
    f => !f.isEmpty && !isContextualTariffField(requirementId, f, sed)
      && (f.path.endsWith(`.${schemaKey}`) || f.label === schemaLabel),
  );
}

function claimAuthorLabel(claim: ExtractedClaim): string {
  return claim.lastWrittenBy?.email ?? claim.lastWrittenBy?.uid ?? 'You';
}

function formatAuthoredDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return undefined;
  }
}

/** Keep numeric fiber inputs in their schema's native units; never convert stored values. */
function formatFiberFieldValue(value: unknown, unit?: string): string {
  const displayValue = formatFieldValue(value);
  return unit && typeof value === 'number' && Number.isFinite(value)
    ? `${displayValue} ${unit}`
    : displayValue;
}

/** One document can extract the same offered MW into both legacy grid data
 * and the worksheet overlay. Consolidate only exact, current source copies;
 * an ordinal provider band or another document's value remains distinct. */
function isDocumentCapacityAlias(sed: SiteEvidenceDocument | undefined, offered: ReportPrimaryField | undefined): boolean {
  if (!sed || !offered || offered.isEmpty || typeof offered.value !== 'number'
    || !Number.isFinite(offered.value) || offered.value < 0) return false;
  if (!['doc', 'doc-override', 'provider+doc'].includes(sed.grid.provenance ?? '')
    && sed.grid.status !== 'verified-from-doc') return false;
  const overlay = resolveAutoFillOverlayField(sed, 'utility-letter', 'capacityOffered_MW');
  const overlaySource = sed.grid.provenanceDetail?.autoFillFieldSources?.capacityOffered_MW;
  const capacitySource = sed.grid.docFieldSources?.capacity;
  const capacity = sed.grid.data?.capacity;
  if (!overlay?.docId || !overlaySource || !capacitySource
    || overlay.docId !== overlaySource.docId || capacitySource.docId !== overlay.docId
    || !Object.is(overlay.value, offered.value) || !Object.is(overlaySource.value, offered.value)
    || !Object.is(capacitySource.value, capacity) || typeof capacity !== 'string') return false;
  const mw = /^((?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?)\s*MW$/i.exec(capacity.trim());
  return Boolean(mw && Number(mw[1]!.replace(/,/g, '')) === offered.value);
}

/** The provider's nearestIx* keys can describe its colocation fallback. */
function relatedFieldPresentation(
  field: ResolvedField,
  sed?: SiteEvidenceDocument,
): Pick<ReportRelatedField, 'label' | 'displayValue'> & Partial<Pick<ReportRelatedField, 'isEmpty'>> {
  const displayValue = formatFieldValue(field.value, field.path);
  if (field.path === 'grid.capacity') {
    return { label: typeof field.value === 'string' && /^(?:High|Limited|Constrained)$/.test(field.value)
      ? 'Grid screening band' : field.provenance === 'doc' || field.provenance === 'doc-override' || field.provenance === 'provider+doc'
        ? 'Document-stated grid capacity' : 'Grid capacity context', displayValue };
  }
  if (isQueryWetlandField(sed, field.path)) {
    if (field.path === 'environmental.hasWetlands') {
      return { label: 'Mapped wetlands in query region', displayValue: field.isEmpty ? displayValue
        : field.value === true ? 'Yes · parcel overlap unconfirmed' : 'No mapped wetlands in covered query' };
    }
    if (field.path === 'environmental.wetlandAcres') {
      const acres = resolveWetlandAcreage(sed?.environmental?.data);
      const isEmpty = field.isEmpty || acres === undefined;
      return { label: 'Wetland source-feature total (acres)', isEmpty,
        displayValue: isEmpty ? 'Unknown — source-feature acreage unavailable'
          : `${formatFieldValue(acres)} acres · not parcel-overlap acreage` };
    }
    return { label: 'Mapped wetland type in query region', displayValue: field.isEmpty ? displayValue
      : `${displayValue} · parcel overlap unconfirmed` };
  }
  const connectivity = sed?.connectivity?.data;
  const role = connectivity?.nearestIxKind === 'colo-fallback'
    ? 'colocation facility' : 'internet exchange';
  if (field.path === 'connectivity.nearestIxName') {
    // Provider payloads can carry the city even in legacy SED types. Read
    // only the saved string; do not infer it from the site or facility name.
    const city = connectivity && 'nearestIxCity' in connectivity
      && typeof connectivity.nearestIxCity === 'string'
      ? connectivity.nearestIxCity.trim() : '';
    return { label: `Nearest ${role}`, displayValue: !field.isEmpty && city
      ? `${displayValue} · ${formatFieldValue(city)}` : displayValue };
  }
  if (field.path === 'connectivity.nearestIxDistKm') {
    return { label: `Geographic distance to ${role}`,
      displayValue: formatFiberFieldValue(field.value, 'km') };
  }
  return { label: field.label, displayValue };
}

export function resolveReportFields(args: {
  requirement: ChecklistItem;
  sed?: SiteEvidenceDocument;
  authoredClaim?: ExtractedClaim;
}): ResolveReportFieldsResult {
  const { requirement, sed, authoredClaim } = args;
  const schema = requirement.authoredSchema ?? [];
  const sedFields = sed ? resolveRequirementFields(sed, requirement.id, true) : [];
  const matchedSedPaths = new Set<string>();
  const primary: ReportPrimaryField[] = [];

  const structuredFields = schema.filter(f => f.key !== 'notes');
  const notesField = schema.find(f => f.key === 'notes');

  for (const field of structuredFields) {
    // Native worksheet units distinguish project MGD roles from utility
    // statistics, and site supply pressure from a unitless number.
    const displayUnit = requirement.id === 'fiber-survey'
      || requirement.id === 'municipal-utility-letter'
      || (requirement.id === 'utility-letter' && field.key === 'capacityOffered_MW') ? field.unit : undefined;
    // Schema-aligned paths belong to the editable primary row even when the
    // current SED value is empty. Excluding only populated hits duplicates
    // empty professional paths in the related/context block.
    for (const path of getAlignedFieldPaths(requirement.id, field.key)) {
      const candidate = sedFields.find(f => f.path === path);
      if ((requirement.id !== 'wetland' || !isQueryWetlandField(sed, path))
        && (!candidate || !isContextualTariffField(requirement.id, candidate, sed))) matchedSedPaths.add(path);
    }
    const authoredVal = authoredClaim?.structuredFields?.[field.key];
    if (isAuthoredValuePresent(authoredVal)) {
      const sedHit = findSedFieldForSchemaKey(sed, requirement.id, field.key, field.label, sedFields);
      if (sedHit) matchedSedPaths.add(sedHit.path);
      primary.push({
        key: field.key,
        label: field.label,
        value: authoredVal,
        displayValue: formatFiberFieldValue(authoredVal, displayUnit),
        provenance: 'authored',
        required: Boolean(field.required),
        editable: true,
        isEmpty: false,
        authoredBy: authoredClaim ? claimAuthorLabel(authoredClaim) : undefined,
        authoredAt: formatAuthoredDate(authoredClaim?.lastWrittenAt),
      });
      continue;
    }

    const sedHit = findSedFieldForSchemaKey(sed, requirement.id, field.key, field.label, sedFields);
    if (sedHit) {
      matchedSedPaths.add(sedHit.path);
      primary.push({
        key: field.key,
        label: field.label,
        value: sedHit.value,
        displayValue: formatFiberFieldValue(sedHit.value, displayUnit),
        provenance: sedHit.provenance,
        sourceLabel: sedHit.sourceLabel,
        required: Boolean(field.required),
        editable: true,
        isEmpty: false,
      });
      continue;
    }

    // D4 — the auto-fill overlay. Document extractions for keys with no SED
    // leaf (`bfe_ft`, `serviceDate`, …) live only in
    // `provenanceDetail.autoFillFields`; until this rung existed they counted
    // ZERO toward completeness, so the machine's work moved no gauge and the
    // ledger kept asking the user to author what a document already answered.
    // Order matters: authored beats overlay (a human's value wins), aligned
    // SED beats overlay (materialized evidence carries richer provenance),
    // and a REJECTED review suppresses the overlay entirely inside the
    // resolver. This is the single completeness chokepoint, so counting here
    // propagates to requirement status, coverage and readiness with no
    // further changes.
    const overlayHit = resolveAutoFillOverlayField(sed, requirement.id, field.key);
    if (overlayHit) {
      primary.push({
        key: field.key,
        label: field.label,
        value: overlayHit.value,
        displayValue: formatFiberFieldValue(overlayHit.value, displayUnit),
        provenance: 'doc',
        sourceLabel: 'Extracted from uploaded document',
        required: Boolean(field.required),
        editable: true,
        isEmpty: false,
      });
      continue;
    }

    primary.push({
      key: field.key,
      label: field.label,
      value: undefined,
      displayValue: '—',
      provenance: 'missing',
      required: Boolean(field.required),
      editable: true,
      isEmpty: true,
    });
  }

  if (notesField) {
    const notesVal = authoredClaim?.structuredFields?.notes
      ?? extractNotesFromClaim(authoredClaim);
    const notesFilled = isFieldFilled(notesVal);
    primary.push({
      key: 'notes',
      label: notesField.label,
      value: notesVal,
      displayValue: notesFilled ? formatFieldValue(notesVal) : '—',
      provenance: notesFilled ? 'authored' : 'missing',
      required: Boolean(notesField.required),
      editable: true,
      isEmpty: !notesFilled,
      authoredBy: notesFilled && authoredClaim ? claimAuthorLabel(authoredClaim) : undefined,
      authoredAt: notesFilled ? formatAuthoredDate(authoredClaim?.lastWrittenAt) : undefined,
    });
  }

  if (requirement.id === 'utility-letter' && isDocumentCapacityAlias(sed,
    primary.find(field => field.key === 'capacityOffered_MW'))) matchedSedPaths.add('grid.capacity');

  const related: ReportRelatedField[] = sedFields
    .filter(f => !matchedSedPaths.has(f.path) && !isStructuralSectionExtra(f))
    .map(f => ({
      path: f.path,
      isEmpty: f.isEmpty,
      ...(isContextualTariffField(requirement.id, f, sed) ? { contextOnly: true } : {}),
      ...relatedFieldPresentation(f, sed),
      value: f.value,
      provenance: f.provenance,
      sourceLabel: f.sourceLabel,
      editable: false as const,
    }));

  const structuredFilled = primary.filter(f => f.key !== 'notes' && !f.isEmpty).length;
  const structuredTotal = structuredFields.length;
  const primaryFilled = primary.filter(f => !f.isEmpty).length;
  const relatedFilled = related.filter(f => !f.contextOnly && !f.isEmpty).length;
  const displayFilled = primaryFilled + relatedFilled;
  const displayTotal = primary.length + related.filter(f => !f.contextOnly).length;

  return {
    primary,
    related,
    hasSchema: schema.length > 0,
    structuredFilled,
    structuredTotal,
    statusFilled: displayFilled,
    statusTotal: displayTotal,
  };
}

function extractNotesFromClaim(claim?: ExtractedClaim): string | undefined {
  if (!claim) return undefined;
  const source = claim.sourceSpan ?? claim.claim ?? '';
  const notesIdx = source.indexOf('Notes: ');
  if (notesIdx >= 0) {
    return source.slice(notesIdx + 'Notes: '.length).trim();
  }
  if (claim.authoredKind === 'prose') {
    return source.trim();
  }
  return undefined;
}
